import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { UiDateInputComponent } from '../../atoms/date-input/date-input.component';
import { UiInputComponent } from '../../atoms/input/input.component';
import { UiRadioComponent } from '../../atoms/radio/radio.component';
import { UiTagComponent } from '../../atoms/tag/tag.component';
import { UiTextareaComponent } from '../../atoms/textarea/textarea.component';
import { UiDrawerComponent } from '../../molecules/drawer/drawer.component';
import { UiFileUploadComponent } from '../../molecules/file-upload/file-upload.component';
import type { UiFileItem, UiFileKind } from '../../molecules/file-upload/file-upload.types';
import { UiFormSectionComponent } from '../../molecules/form-section/form-section.component';
import { UiDataTableComponent } from '../data-table/data-table.component';
import {
  DataGridColumn,
  DataTableSegmentedFilter,
  DataTableToolbarSelectOption,
  FilterOptionsModel,
  parseColumnMultiFilter,
  RowAction,
  SortState,
} from '../data-table/data-table.types';
import { UiInvoiceCaptureComponent } from '../invoice-capture/invoice-capture.component';
import type { UiInvoiceCaptureResult } from '../invoice-capture/invoice-capture.types';
import type {
  UiReceiptBalanceMode,
  UiReceiptMode,
  UiReceiptRow,
} from './receipts.types';

type CaptureType = 'Factura' | 'General';

const VALIDATION_CHECKS: readonly {
  title: string;
  items: readonly { text: string; key?: 'names' }[];
}[] = [
  {
    title: 'Datos del emisor',
    items: [{ text: 'El emisor corresponde al proveedor del gasto' }],
  },
  {
    title: 'Datos del receptor',
    items: [
      { text: 'El receptor corresponde a la empresa seleccionada en el gasto' },
      { text: 'Código postal' },
      { text: 'Dirección completa' },
    ],
  },
  {
    title: 'Fechas',
    items: [{ text: 'La fecha del comprobante corresponde al mes vigente' }],
  },
  {
    title: 'Impuestos, retenciones y generales',
    items: [
      { text: 'El uso de CFDI es correcto' },
      { text: 'Impuestos y retenciones desglosados correctamente' },
      { text: 'El archivo xml y pdf son correspondientes entre sí', key: 'names' },
    ],
  },
  {
    title: 'SAT',
    items: [{ text: 'Comprobante vigente en el SAT' }],
  },
];

const MULTI_FILTER_KEYS = [
  'tipo',
  'emisor',
  'metodoPago',
  'formaPago',
  'usoCfdi',
  'validacion',
  'conciliacionDescargaSat',
] as const;

type MultiFilterKey = (typeof MULTI_FILTER_KEYS)[number];

const FIXED_FILTER_OPTIONS: Partial<Record<MultiFilterKey, DataTableToolbarSelectOption[]>> = {
  tipo: [
    { id: 'tipo-cfdi', label: 'CFDI', value: 'CFDI' },
    { id: 'tipo-general', label: 'Comprobante no fiscal', value: 'Comprobante no fiscal' },
  ],
  validacion: [
    { id: 'validacion-validado', label: 'Validado', value: 'Validado' },
    { id: 'validacion-pendiente', label: 'Pendiente de validar', value: 'Pendiente de validar' },
  ],
  conciliacionDescargaSat: [
    { id: 'sat-conciliado', label: 'Conciliado', value: 'Conciliado' },
    { id: 'sat-pendiente', label: 'Pendiente', value: 'Pendiente' },
  ],
};

function money(value: string): number {
  const amount = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
}

function distinctOptions(
  rows: readonly UiReceiptRow[],
  key: 'emisor' | 'metodoPago' | 'formaPago' | 'usoCfdi',
): DataTableToolbarSelectOption[] {
  const values = [...new Set(rows.map(row => row[key]).filter(value => value && value !== '—'))]
    .sort((left, right) => left.localeCompare(right, 'es'));
  return values.map((value, index) => ({
    id: `${key}-${index + 1}`,
    label: value,
    value,
  }));
}

function selectedValues(groups: FilterOptionsModel[], prefix: string): string[] {
  return groups
    .find(group => group.prefix === prefix)
    ?.values.filter(value => value.selected)
    .map(value => value.value) ?? [];
}

@Component({
  selector: 'ui-receipts',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    UiDataTableComponent,
    UiDateInputComponent,
    UiDrawerComponent,
    UiFileUploadComponent,
    UiFormSectionComponent,
    UiInputComponent,
    UiInvoiceCaptureComponent,
    UiRadioComponent,
    UiTagComponent,
    UiTextareaComponent,
  ],
  templateUrl: './receipts.component.html',
  styleUrl: './receipts.component.scss',
})
export class UiReceiptsComponent {
  readonly receiptMode = input.required<UiReceiptMode>();
  readonly balanceMode = input.required<UiReceiptBalanceMode>();
  readonly expectedSubtotal = input.required<number>();
  readonly deliveredSubtotal = input.required<number>();
  readonly rows = model<UiReceiptRow[]>([]);
  readonly loading = input(false);
  /** Oculta Nuevo, Validar y Eliminar. XML y PDF siguen disponibles. */
  readonly readOnly = input(false);

  readonly xmlClick = output<UiReceiptRow>();
  readonly pdfClick = output<UiReceiptRow>();
  readonly exportExcel = output<void>();

  private readonly invoiceCapture = viewChild<UiInvoiceCaptureComponent>('invoiceCapture');
  private readonly captureSelection = signal<CaptureType>('Factura');
  private readonly activeValidations = signal<string[]>([]);
  private readonly activeConciliaciones = signal<string[]>([]);
  private readonly activeColumnFilters = signal<Record<string, string>>({});
  private readonly sortState = signal<SortState | null>(null);
  private editingId: string | null = null;

  protected readonly validationChecks = VALIDATION_CHECKS;
  protected readonly namesMatch = signal(true);
  protected readonly drawerOpen = signal(false);
  protected readonly drawerTitle = signal('Nuevo comprobante');
  protected readonly fecha = signal('');
  protected readonly importe = signal('');
  protected readonly documentos = signal<UiFileItem[]>([]);
  protected readonly notas = signal('');
  protected readonly files = signal<UiFileItem[]>([]);
  protected readonly pdfAccept: UiFileKind[] = ['pdf'];
  protected readonly generalError = signal<string | null>(null);

  protected readonly filterGroups = signal<FilterOptionsModel[]>([
    {
      prefix: 'validacion',
      name: 'Validación',
      values: [
        { name: 'Validado', value: 'Validado' },
        { name: 'Pendiente de validar', value: 'Pendiente de validar' },
      ],
    },
    {
      prefix: 'conciliacionSat',
      name: 'Conciliación SAT',
      values: [
        { name: 'Conciliado', value: 'Conciliado' },
        { name: 'Pendiente', value: 'Pendiente' },
      ],
    },
  ]);

  protected readonly segmentedFilters = signal<DataTableSegmentedFilter[]>([
    {
      key: 'tipo',
      label: 'Tipo',
      value: 'all',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'CFDI', label: 'CFDI' },
        { value: 'Comprobante no fiscal', label: 'Comprobante no fiscal' },
      ],
    },
  ]);

  protected readonly isBothMode = computed(() => this.receiptMode() === 'both');
  protected readonly hasCfdi = computed(() => this.receiptMode() !== 'general');
  protected readonly requiresDelivery = computed(() => this.balanceMode() === 'requires-delivery');
  protected readonly captureType = computed<CaptureType>(() => {
    if (this.receiptMode() === 'cfdi') return 'Factura';
    if (this.receiptMode() === 'general') return 'General';
    return this.captureSelection();
  });
  protected readonly showFactura = computed(() => this.captureType() === 'Factura');
  protected readonly showGeneral = computed(() => this.captureType() === 'General');
  protected readonly visibleFilterGroups = computed(() =>
    this.hasCfdi()
      ? this.filterGroups()
      : this.filterGroups().filter(group => group.prefix !== 'conciliacionSat'),
  );

  private readonly modeRows = computed(() => {
    const mode = this.receiptMode();
    if (mode === 'cfdi') return this.rows().filter(row => row.tipo === 'CFDI');
    if (mode === 'general') {
      return this.rows().filter(row => row.tipo === 'Comprobante no fiscal');
    }
    return this.rows();
  });

  protected readonly displayedRows = computed(() => {
    const segmentedType = this.isBothMode()
      ? this.segmentedFilters().find(filter => filter.key === 'tipo')?.value ?? 'all'
      : 'all';
    const filtered = this.modeRows().filter(row => {
      const tipoOk = segmentedType === 'all' || row.tipo === segmentedType;
      const validationOk = this.activeValidations().length === 0
        || this.activeValidations().includes(row.validacion);
      const satOk = !this.hasCfdi()
        || this.activeConciliaciones().length === 0
        || this.activeConciliaciones().includes(row.conciliacionDescargaSat);
      const columnOk = Object.entries(this.activeColumnFilters()).every(([key, value]) => {
        if (!value || !MULTI_FILTER_KEYS.includes(key as MultiFilterKey)) return true;
        const selected = parseColumnMultiFilter(value);
        if (selected.length === 0) return true;
        return selected.includes(String(row[key as keyof UiReceiptRow] ?? ''));
      });
      return tipoOk && validationOk && satOk && columnOk;
    });
    const sort = this.sortState();
    if (!sort) return filtered;
    const numeric = new Set([
      'subtotal',
      'descuento',
      'retencionIsr',
      'retencionIva',
      'iva',
      'neto',
    ]);
    return [...filtered].sort((left, right) => {
      const a = left[sort.sortBy as keyof UiReceiptRow];
      const b = right[sort.sortBy as keyof UiReceiptRow];
      if (numeric.has(sort.sortBy)) {
        return sort.order === 'asc' ? Number(a) - Number(b) : Number(b) - Number(a);
      }
      return sort.order === 'asc'
        ? String(a ?? '').localeCompare(String(b ?? ''), 'es')
        : String(b ?? '').localeCompare(String(a ?? ''), 'es');
    });
  });

  protected readonly columns = computed<DataGridColumn[]>(() => {
    const saved = this.modeRows();
    const multi = (key: MultiFilterKey): Pick<DataGridColumn, 'filterType' | 'filterOptions'> => ({
      filterType: 'multiselect',
      filterOptions: FIXED_FILTER_OPTIONS[key] ?? distinctOptions(
        saved,
        key as 'emisor' | 'metodoPago' | 'formaPago' | 'usoCfdi',
      ),
    });
    const columns: DataGridColumn[] = [
      { key: 'fecha', header: 'Fecha', type: 'date', width: 120 },
      { key: 'numero', header: 'Número', type: 'string', width: 120 },
      { key: 'tipo', header: 'Tipo', type: 'string', width: 190, ...multi('tipo') },
      { key: 'rfcEmisor', header: 'RFC Emisor', type: 'string', width: 150 },
      { key: 'emisor', header: 'Emisor', type: 'string', width: 260, truncate: true, ...multi('emisor') },
      { key: 'subtotal', header: 'Subtotal', type: 'currency', width: 130 },
      { key: 'descuento', header: 'Descuento', type: 'currency', width: 130 },
      { key: 'retencionIsr', header: 'Retención ISR', type: 'currency', width: 140 },
      { key: 'retencionIva', header: 'Retención IVA', type: 'currency', width: 140 },
      { key: 'iva', header: 'IVA', type: 'currency', width: 120 },
      { key: 'neto', header: 'Neto', type: 'currency', width: 130 },
      { key: 'metodoPago', header: 'Método de pago', type: 'string', width: 150, ...multi('metodoPago') },
      { key: 'formaPago', header: 'Forma de pago', type: 'string', width: 180, ...multi('formaPago') },
      { key: 'usoCfdi', header: 'Uso CFDI', type: 'string', width: 110, ...multi('usoCfdi') },
      {
        key: 'validacion',
        header: 'Validación',
        type: 'string',
        width: 180,
        ...multi('validacion'),
        tagColorMap: {
          Validado: 'success',
          'Pendiente de validar': 'warning',
        },
      },
    ];
    if (this.hasCfdi()) {
      columns.push({
        key: 'conciliacionDescargaSat',
        header: 'Conciliación SAT',
        type: 'string',
        width: 180,
        ...multi('conciliacionDescargaSat'),
        tagColorMap: {
          Conciliado: 'success',
          Pendiente: 'warning',
        },
      });
    }
    return columns;
  });

  protected readonly actionItems: RowAction[] = [
    {
      key: 'validar',
      label: 'Validar',
      visible: row =>
        !this.readOnly() && (row as UiReceiptRow).validacion === 'Pendiente de validar',
    },
    {
      key: 'xml',
      label: 'XML',
      visible: row => (row as UiReceiptRow).tipo === 'CFDI',
    },
    {
      key: 'pdf',
      label: 'PDF',
      visible: row => (row as UiReceiptRow).tipo === 'CFDI',
    },
    {
      key: 'eliminar',
      label: 'Eliminar',
      destructive: true,
      visible: () => !this.readOnly(),
    },
  ];

  protected readonly receiptsSubtotal = computed(() => this.sumColumn('subtotal'));
  protected readonly cfdiSubtotal = computed(() => this.sumCfdiColumn('subtotal'));
  protected readonly receiptsIva = computed(() => this.sumCfdiColumn('iva'));
  protected readonly receiptsRetencionIsr = computed(() => this.sumCfdiColumn('retencionIsr'));
  protected readonly receiptsRetencionIva = computed(() => this.sumCfdiColumn('retencionIva'));
  protected readonly receiptsAmount = computed(() => this.sumCfdiColumn('neto'));
  protected readonly differenceAmount = computed(
    () => this.expectedSubtotal() - this.receiptsSubtotal(),
  );
  protected readonly importesValidated = computed(
    () => Math.abs(this.differenceAmount()) < 0.005,
  );
  protected readonly balanceAmount = computed(() => {
    const expected = this.requiresDelivery() ? this.deliveredSubtotal() : this.expectedSubtotal();
    return expected - this.receiptsSubtotal();
  });
  protected readonly balanceSettled = computed(() => Math.abs(this.balanceAmount()) < 0.005);
  protected readonly deliveryInvoiced = computed(
    () =>
      this.balanceSettled() &&
      Math.abs(this.deliveredSubtotal() - this.expectedSubtotal()) < 0.005,
  );
  protected readonly canAddReceipt = computed(() => this.balanceAmount() > 0.005);

  protected formatMoney(value: number): string {
    return value.toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
    });
  }

  protected selectTipo(value: CaptureType): void {
    if (!this.isBothMode()) return;
    this.generalError.set(null);
    this.captureSelection.set(value);
  }

  protected onStatusFilter(groups: FilterOptionsModel[]): void {
    this.filterGroups.set(groups.map(group => ({ ...group, values: [...group.values] })));
    this.activeValidations.set(selectedValues(groups, 'validacion'));
    this.activeConciliaciones.set(
      this.hasCfdi() ? selectedValues(groups, 'conciliacionSat') : [],
    );
  }

  protected onColumnFilters(filters: Record<string, string>): void {
    this.activeColumnFilters.set(filters);
  }

  protected onSegmentedFilter(filters: DataTableSegmentedFilter[]): void {
    if (this.isBothMode()) this.segmentedFilters.set(filters);
  }

  protected onSort(event: SortState): void {
    this.sortState.set(event);
  }

  protected onNew(): void {
    this.resetDrawer();
    this.drawerTitle.set('Nuevo comprobante');
    this.drawerOpen.set(true);
  }

  protected onAction(event: { row: unknown; action: RowAction }): void {
    const row = event.row as UiReceiptRow;
    if (event.action.key === 'eliminar') {
      this.rows.update(rows => rows.filter(item => item.id !== row.id));
      return;
    }
    if (event.action.key === 'xml') this.xmlClick.emit(row);
    if (event.action.key === 'pdf') this.pdfClick.emit(row);
  }

  protected onDrawerSave(): void {
    if (this.showFactura()) {
      const capture = this.invoiceCapture();
      const result = capture?.takeResult();
      if (!result) return;
      const validated = result.items.filter(item => item.validacion === 'XML válido');
      if (validated.length === 0) {
        capture?.reportSaveError('No hay comprobantes validados para guardar.');
        return;
      }
      this.saveCapture({ ...result, items: validated });
    } else {
      const row = this.rowFromGeneral();
      if (!row) return;
      this.upsert(row);
    }
    this.drawerOpen.set(false);
    this.resetDrawer();
  }

  protected onDrawerClosed(): void {
    this.resetDrawer();
  }

  private saveCapture(result: UiInvoiceCaptureResult): void {
    const timestamp = Date.now();
    result.items.forEach((item, index) => {
      const id = this.editingId && index === 0
        ? this.editingId
        : `receipt-${timestamp}-${index + 1}`;
      this.upsert({
        id,
        fecha: item.fecha,
        numero: item.folio || '—',
        tipo: 'CFDI',
        rfcEmisor: item.rfcEmisor === '—' ? '' : item.rfcEmisor,
        emisor: item.emisor === '—' ? '' : item.emisor,
        subtotal: money(item.subtotal),
        descuento: money(item.descuento),
        retencionIsr: money(item.retencionIsr),
        retencionIva: money(item.retencionIva),
        iva: money(item.iva),
        neto: money(item.importe),
        metodoPago: item.metodoPago === '—' ? '' : item.metodoPago,
        formaPago: item.formaPago === '—' ? '' : item.formaPago,
        usoCfdi: item.usoCfdi === '—' ? '' : item.usoCfdi,
        validacion: 'Validado',
        conciliacionDescargaSat: 'Pendiente',
      });
    });
  }

  private rowFromGeneral(): UiReceiptRow | null {
    const fecha = this.fecha().trim();
    const importeText = this.importe().trim();
    if (!fecha || !importeText) {
      this.generalError.set('Captura la fecha y el importe para guardar.');
      return null;
    }
    const importe = money(importeText);
    if (importe - this.balanceAmount() > 0.005) {
      this.generalError.set(
        this.requiresDelivery()
          ? 'El importe supera el saldo por facturar.'
          : 'El importe supera el saldo subtotal.',
      );
      return null;
    }
    this.generalError.set(null);
    const current = this.rows().find(row => row.id === this.editingId);
    return {
      id: this.editingId ?? `receipt-${Date.now()}`,
      fecha,
      numero: current?.numero ?? '—',
      tipo: 'Comprobante no fiscal',
      rfcEmisor: current?.rfcEmisor ?? '',
      emisor: current?.emisor ?? '',
      subtotal: current?.subtotal ?? importe,
      descuento: current?.descuento ?? 0,
      retencionIsr: current?.retencionIsr ?? 0,
      retencionIva: current?.retencionIva ?? 0,
      iva: current?.iva ?? 0,
      neto: importe,
      metodoPago: current?.metodoPago ?? '',
      formaPago: current?.formaPago ?? '',
      usoCfdi: current?.usoCfdi ?? '',
      validacion: current?.validacion ?? 'Pendiente de validar',
      conciliacionDescargaSat: current?.conciliacionDescargaSat ?? 'Pendiente',
    };
  }

  private upsert(row: UiReceiptRow): void {
    this.rows.update(rows => {
      const index = rows.findIndex(item => item.id === row.id);
      return index === -1
        ? [...rows, row]
        : rows.map(item => item.id === row.id ? row : item);
    });
  }

  private sumColumn(
    key: 'subtotal' | 'iva' | 'retencionIsr' | 'retencionIva' | 'neto',
  ): number {
    return this.modeRows().reduce((sum, row) => sum + row[key], 0);
  }

  private sumCfdiColumn(
    key: 'subtotal' | 'iva' | 'retencionIsr' | 'retencionIva' | 'neto',
  ): number {
    return this.rows()
      .filter(row => row.tipo === 'CFDI')
      .reduce((sum, row) => sum + row[key], 0);
  }

  private resetDrawer(): void {
    this.editingId = null;
    this.captureSelection.set('Factura');
    this.fecha.set('');
    this.importe.set('');
    this.documentos.set([]);
    this.notas.set('');
    this.files.set([]);
    this.generalError.set(null);
    this.invoiceCapture()?.clearCaptured();
  }
}
